import { useState } from 'react';
import axios from 'axios';
import { mensagemDeErro } from '../../utils/Utils';
import { Transacao } from './typeTransacao';
import { useTransacaoContexto } from '../../contextos/TransacaoContexto';
import { primeiroDiaMesAtual, ultimoDiaMesAtual } from '../../utils/Utils';
import useAvisos from '../../hooks/useAviso';

const API_URL = process.env.REACT_APP_API_URL + '/transacoes';

export function useTransacoes(atualizarGlobal = true) {
  const { transacoes, setTransacoes } = useTransacaoContexto();

  const { adicionarAviso } = useAvisos();

  const [loading, setLoading] = useState<boolean>(false);

  const parseDate = (dateStr: string | Date | null | undefined): Date | null => {
    if (!dateStr) return null;

    if (typeof dateStr === 'string') {
      const parts = dateStr.split('/');
      if (parts.length !== 3) return null; // Verifica se a string está no formato esperado

      const [day, month, year] = parts.map(Number);
      return new Date(year, month - 1, day); // Mês começa do 0 no JS
    } else {
      return dateStr;
    }
  };

  // Função auxiliar para obter o token JWT do localStorage
  const getToken = () => localStorage.getItem('token');

  // Fetch all transacoes with JWT token and optional filters
  const fetchTransacoes = async (filtros?: {
    tipo?: string;
    dataInicial?: Date | string;
    dataFinal?: Date | string;
    descricao?: string;
    valorInicial?: number;
    valorFinal?: number;
    tipoPagamento?: string;
    conta?: number | string;
    categoria?: number | string;
    cartao?: number | string;
  }) => {
    if (loading) {
      return;
    }

    const token = getToken();
    if (!token) {
      adicionarAviso('Usuário não autenticado.', 'erro');
      return;
    }

    setLoading(true);

    try {
      // Construir a query string a partir dos filtros fornecidos
      const response = await axios.get<Transacao[]>(API_URL, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          // Os filtros são passados como query parameters aqui
          tipo: filtros?.tipo,
          dataInicial: filtros?.dataInicial ? parseDate(filtros?.dataInicial) : primeiroDiaMesAtual(),
          dataFinal: filtros?.dataFinal ? parseDate(filtros?.dataFinal) : ultimoDiaMesAtual(),
          descricao: filtros?.descricao,
          valorInicial: filtros?.valorInicial,
          valorFinal: filtros?.valorFinal,
          tipoPagamento: filtros?.tipoPagamento,
          contaId: filtros?.conta,
          categoriaId: filtros?.categoria,
          cartaoCreditoId: filtros?.cartao
        }
      });

      if (atualizarGlobal) {
        setTransacoes(response.data);
      }

      return response.data;
    } catch (err) {
      adicionarAviso('Não foi possível carregar as movimentações.');
    } finally {
      setLoading(false);
    }
  };

  // Create a new transacao with JWT token
  const createTransacao = async (newTransacao: Transacao) => {
    const token = getToken();
    if (!token) {
      adicionarAviso('Usuário não autenticado.', 'erro');
      return;
    }

    setLoading(true);

    try {
      const registro = await axios.post(API_URL, newTransacao, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (registro.status === 201) {
        setTransacoes((prevTransacoes) => [...prevTransacoes, registro.data]);
      }

      adicionarAviso('Movimentação registrada.', 'sucesso');
    } catch (err) {
      const mensagem = mensagemDeErro(err);
      if (mensagem) {
        adicionarAviso(mensagem);
      } else {
        adicionarAviso('Não foi possível registrar a movimentação.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Update a transacao with JWT token
  const updateTransacao = async (id: number, updatedTransacao: Transacao) => {
    const token = getToken();
    if (!token) {
      adicionarAviso('Usuário não autenticado.', 'erro');
      return;
    }

    setLoading(true);

    try {
      const registro = await axios.put(`${API_URL}/${id}`, updatedTransacao, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setTransacoes((prevTransacoes) => prevTransacoes.map((transacao) => (transacao.id === id ? registro.data : transacao)));

      adicionarAviso('Movimentação atualizada.', 'sucesso');
    } catch (err) {
      adicionarAviso('Não foi possível atualizar a movimentação.');
    } finally {
      setLoading(false);
    }
  };

  const bulkUpdateTransacao = async (
    listaIds: string[],
    categoriaId?: string | null,
    contaId?: string | null,
    cartaoCreditoId?: string | null,
    tipoPagamento?: string | null
  ) => {
    const token = getToken();
    if (!token) {
      adicionarAviso('Usuário não autenticado.', 'erro');
      return;
    }

    setLoading(true);

    // Monta dinamicamente apenas os campos informados
    const payload: { ids: string[]; categoriaId?: number; contaId?: number; cartaoCreditoId?: number; tipoPagamento?: string } = { ids: listaIds };

    if (categoriaId) payload.categoriaId = parseInt(categoriaId);
    if (contaId) payload.contaId = parseInt(contaId);
    if (cartaoCreditoId) payload.cartaoCreditoId = parseInt(cartaoCreditoId);
    if (tipoPagamento) payload.tipoPagamento = tipoPagamento;

    try {
      // Backend .NET expõe bulk update como PATCH /transacoes/lote.
      await axios.patch(`${API_URL}/lote`, payload, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setTransacoes((prevTransacoes) =>
        prevTransacoes.map((transacao) => (listaIds.includes(transacao.id?.toString() || '') ? { ...transacao, ...payload } : transacao))
      );

      adicionarAviso('Movimentações atualizadas.', 'sucesso');
    } catch (err) {
      adicionarAviso('Não foi possível atualizar as movimentações.');
    } finally {
      setLoading(false);
    }
  };

  // Delete a transacao with JWT token
  const deleteTransacao = async (listaIds: string[]) => {
    const token = getToken();
    if (!token) {
      adicionarAviso('Usuário não autenticado.', 'erro');
      return;
    }

    setLoading(true);

    try {
      await axios.delete(`${API_URL}`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: {
          ids: listaIds
        }
      });
      setTransacoes((prevTransacoes) => prevTransacoes.filter((transacao) => !listaIds.includes(transacao.id?.toString() || '')));

      adicionarAviso('Movimentações excluídas.', 'sucesso');
    } catch (err) {
      const mensagem = mensagemDeErro(err);
      if (mensagem) {
        adicionarAviso(mensagem);
      } else {
        adicionarAviso('Não foi possível excluir a movimentação.');
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    transacoes,
    loading,
    fetchTransacoes,
    createTransacao,
    updateTransacao,
    bulkUpdateTransacao,
    deleteTransacao
  };
}

